<script setup lang="ts">
/**
 * SignIn.vue — the sign-in chooser, for a browser with no session.
 *
 * Shows exactly the ways in this environment has switched on
 * (GET /api/auth/providers): Microsoft, Google, Lark, email. While only Lark
 * is on it is the page it always was — "Open in Lark" first, because the
 * client signs a customer in with no consent screen and no password, and
 * "Continue in browser" second. Inside the Lark client this page never shows:
 * the store signs the person in silently.
 *
 * Email is two steps on one page: the address (with Turnstile), then "check
 * your inbox" with a field for the 6-digit code — the link in the mail signs
 * in by itself (stores/auth.ts reads `#/auth/email?c=…&t=…`), the code is for
 * whoever reads their mail on another device.
 *
 * An invitation into an organisation account (`#/invite?i=…&t=…`, kept by the
 * store) shows who invited the person and to which account, and only the button
 * of that account's platform: the sign-in carries the invitation. A sign-in an
 * admin must approve first (JOIN_REQUESTED) says so; a Google one may be retried
 * as a Workspace super admin, who always gets in.
 *
 * The legal links stay on this page: it is all this domain serves to a visitor
 * without a session, and Paddle's domain review must reach the terms, privacy
 * and refund policies from the checkout domain.
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ExternalLink, Mail, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { WWW_ORIGIN } from '@/utils/links'
import { larkAppLink } from '@/utils/larkAppLink'
import { useTurnstile } from '@/composables/useTurnstile'
import { platformName } from '@/utils/platformName'
import AdminApprovalRequired from '@/components/AdminApprovalRequired.vue'
import { MICROSOFT_ADMIN_PAGE_PATH } from '@lark-automadoc/shared-types'

const { t, te, locale } = useI18n()
const auth = useAuthStore()

/** Render a stored message: our own keys translate, a server sentence shows as sent. */
function message(value: string | null): string {
  if (!value) return ''
  return te(value) ? t(value) : value
}

const options = computed(() => auth.signInOptions)
const providersKnown = computed(() => auth.providers !== null)
/**
 * Only Lark on in this environment: the page as it was before other platforms.
 * Never a guess: while the providers are unknown the page shows placeholders,
 * and when they could not be read it says so and offers a retry.
 */
const larkOnly = computed(() => providersKnown.value && !options.value.microsoft && !options.value.google && !options.value.email)

const legalLinks = computed(() => {
  const base = `${WWW_ORIGIN}/${locale.value}`
  return [
    { label: t('footerTermsOfService'), href: `${base}/terms` },
    { label: t('footerPrivacyPolicy'), href: `${base}/privacy` },
    { label: t('footerRefundPolicy'), href: `${base}/refund` },
  ]
})

// ---------------------------------------------------------------------------
// An invitation, and a request waiting for an admin
// ---------------------------------------------------------------------------

const invite = computed(() => auth.invitationPreview)
/** The one platform an invitation signs in with; null shows every way in. */
const onlyPlatform = computed(() => invite.value?.platform ?? null)
const allows = (platform: 'lark' | 'microsoft' | 'google' | 'email') => !onlyPlatform.value || onlyPlatform.value === platform

const inviteAccount = computed(() => invite.value?.accountName || invite.value?.organisation?.name || 'AutomaDoc')
const inviteRole = computed(() => (invite.value?.role === 'admin' ? t('inviteRoleAdmin') : t('inviteRoleMember')))
const inviteOrg = computed(() => invite.value?.organisation?.name || invite.value?.organisation?.id || inviteAccount.value)

const joinWaiting = computed(() => auth.error === 'authErrorJoinRequested')

/** A Microsoft sign-in the organisation's consent policy stopped (MS_CONSENT_REQUIRED). */
const consentBlocked = computed(() => auth.error === 'authErrorMicrosoftConsent')
function backFromConsent(): void {
  auth.error = null
  auth.signInConsentUrl = null
}

/** Which button started a redirect, so only that one says "Redirecting…". */
const pending = ref<'lark' | 'microsoft' | 'google' | null>(null)

const retrying = ref(false)
async function retry(): Promise<void> {
  retrying.value = true
  try {
    await auth.retryProviders()
  } finally {
    retrying.value = false
  }
}

function start(provider: 'lark' | 'microsoft' | 'google'): void {
  pending.value = provider
  void auth.startSignIn(provider, { loginHint: provider === auth.hints.provider ? auth.hints.hint : undefined })
}

// ---------------------------------------------------------------------------
// Email
// ---------------------------------------------------------------------------

const emailStep = ref<'enter' | 'sent'>('enter')
const email = ref(auth.hints.provider === 'email' ? (auth.hints.hint ?? '') : '')
const code = ref('')
const challengeId = ref<string | null>(null)
const emailBusy = ref(false)
const emailError = ref<string | null>(null)
const turnstileEl = ref<HTMLElement | null>(null)
const turnstile = useTurnstile(
  turnstileEl,
  () => options.value.turnstileSiteKey,
  () => locale.value,
)

const emailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim()))
/** Without a site key the API decides (a development API may not ask); with one, wait for the widget. */
const canSend = computed(
  () => emailValid.value && !emailBusy.value && (!options.value.turnstileSiteKey || !!turnstile.token.value),
)
const codeValid = computed(() => /^\d{6}$/.test(code.value.trim()))

async function sendEmail(): Promise<void> {
  if (!canSend.value) return
  emailBusy.value = true
  emailError.value = null
  try {
    const started = await auth.emailStart(email.value.trim(), turnstile.token.value ?? '')
    challengeId.value = started.challengeId
    emailStep.value = 'sent'
    code.value = ''
  } catch (err) {
    emailError.value = err instanceof Error ? err.message : 'authFailed'
  } finally {
    emailBusy.value = false
    turnstile.reset()
  }
}

async function verifyCode(): Promise<void> {
  if (!codeValid.value || !challengeId.value || emailBusy.value) return
  emailBusy.value = true
  emailError.value = null
  try {
    await auth.emailVerifyCode(challengeId.value, code.value.trim())
  } catch (err) {
    emailError.value = err instanceof Error ? err.message : 'authFailed'
  } finally {
    emailBusy.value = false
  }
}

function useAnotherEmail(): void {
  emailStep.value = 'enter'
  challengeId.value = null
  code.value = ''
  emailError.value = null
  void nextTick(() => turnstile.mount())
}

// The widget mounts once its container exists: the email form appears only
// after the providers answered that email is on.
watch(
  () => [options.value.email, options.value.turnstileSiteKey, emailStep.value, consentBlocked.value] as const,
  () => void nextTick(() => turnstile.mount()),
)

onMounted(() => {
  void auth.loadProviders()
  void auth.loadInvitationPreview()
  void nextTick(() => turnstile.mount())
})

const buttonClass =
  'flex items-center justify-center gap-2.5 w-full px-5 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60 disabled:cursor-wait'
</script>

<template>
  <div class="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
    <main class="flex-1 flex flex-col items-center justify-center px-4 py-10 sm:p-8">
      <div class="w-full max-w-sm">
        <!-- Logo and one line: the buttons are the page. -->
        <div class="flex flex-col items-center gap-3 mb-6">
          <img src="/logo.svg" alt="AutomaDoc" class="h-10 w-10" />
          <h1 class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ t('signInPortalHeading') }}</h1>
        </div>

        <!-- A previous attempt failed (state mismatch, a refused code, a revoked
             session) or something worth saying happened (an admin's consent). -->
        <!-- An invitation: who sent it, to which account, and how to accept it. -->
        <div v-if="invite" class="mb-4 rounded-lg border border-gray-200 bg-white px-3 py-3 text-sm dark:border-white/10 dark:bg-gray-800">
          <p class="text-gray-900 dark:text-gray-100">
            {{ invite.inviterName ? t('inviteIntro', { inviter: invite.inviterName, account: inviteAccount, role: inviteRole }) : t('inviteIntroNoInviter', { account: inviteAccount, role: inviteRole }) }}
          </p>
          <p v-if="invite.organisation" class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ t('inviteSignInWith', { platform: platformName(invite.platform, t), org: inviteOrg }) }}</p>
          <button type="button" class="mt-2 text-xs text-gray-500 underline hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" @click="auth.dismissInvitation()">{{ t('inviteUseAnotherWay') }}</button>
        </div>

        <!-- Waiting for an admin: not a failure, so not red. -->
        <div v-if="joinWaiting" role="status" class="mb-4 rounded-lg bg-amber-50 px-3 py-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-100">
          <p>{{ t('authErrorJoinRequested') }}</p>
          <button
            v-if="auth.joinRequested?.provider === 'google' && options.google"
            type="button"
            class="mt-2 text-xs font-medium underline"
            @click="auth.startSignIn('google', { adminProof: true })"
          >{{ t('signInWorkspaceSuperAdmin') }}</button>
        </div>
        <!-- Microsoft sign-in stopped by the organisation's consent policy: a page of its
             own, the link for their IT admin and nothing else to choose until they go back. -->
        <AdminApprovalRequired v-else-if="consentBlocked" :consent-url="auth.signInConsentUrl" after="signin" class="mb-4" />
        <p v-else-if="auth.error" role="alert" class="text-sm text-red-600 dark:text-red-400 mb-4 text-center">
          {{ message(auth.error) }}
          <!-- Backing out of Microsoft's "needs admin approval" screen reads as a plain cancel. -->
          <span v-if="auth.microsoftDeclined && auth.error === 'authErrorCancelled'" class="mt-1 block text-xs text-gray-600 dark:text-gray-400">
            {{ t('signInMicrosoftDeclinedHint') }}
            <a :href="`#${MICROSOFT_ADMIN_PAGE_PATH}`" class="font-medium underline">{{ t('officeDeployApprovalLink') }}</a>
          </span>
        </p>
        <p v-if="auth.notice === 'adminConsentDone'" role="status" class="text-sm text-green-700 dark:text-green-400 mb-4 text-center">{{ t('signInAdminConsentDone') }}</p>

        <div v-if="consentBlocked" class="text-center">
          <button type="button" :class="buttonClass" @click="backFromConsent">
            <ArrowLeft :size="16" aria-hidden="true" />
            {{ t('adminApprovalBack') }}
          </button>
        </div>

        <!-- The ways in are not known yet: placeholders, not a guess. -->
        <div v-else-if="!providersKnown && !auth.providersFailed" class="space-y-3" role="status" :aria-label="t('loading')">
          <div v-for="n in 3" :key="n" class="h-11 w-full animate-pulse rounded-lg bg-gray-200 dark:bg-gray-800" />
        </div>

        <!-- They could not be read: say so, and try again. Not every button
             instead: Microsoft and Google need the client ids that answer carries. -->
        <div v-else-if="!providersKnown" class="space-y-3 text-center">
          <p role="alert" class="text-sm text-gray-700 dark:text-gray-300">{{ t('signInProvidersFailed') }}</p>
          <button type="button" :class="buttonClass" :disabled="retrying" @click="retry">
            <Loader2 v-if="retrying" :size="16" class="animate-spin" aria-hidden="true" />
            {{ t('retry') }}
          </button>
        </div>

        <!-- Lark only: the page as it always was. Opening in Lark is the primary
             action; each button says what it does, not just where it goes. -->
        <div v-else-if="larkOnly && allows('lark')" class="space-y-3">
          <!-- Opening the Lark app would leave the invitation behind in this tab. -->
          <div v-if="!invite">
            <a
              :href="larkAppLink()"
              class="flex items-center justify-center gap-2 w-full px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ExternalLink :size="16" />
              {{ t('openInLark') }}
            </a>
            <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400 text-center">{{ t('signInOpenInLarkHint') }}</p>
          </div>

          <div v-if="auth.canUseOAuth">
            <button
              @click="start('lark')"
              :disabled="auth.oauthRedirecting"
              class="flex items-center justify-center gap-2 w-full px-5 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-60 disabled:cursor-wait"
            >
              <img src="/lark-logo.png" alt="" aria-hidden="true" class="h-4 w-4 shrink-0" />
              {{ auth.oauthRedirecting ? t('signInRedirecting') : t('signInContinueWithLark') }}
            </button>
            <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400 text-center">{{ t('signInBrowserHint') }}</p>
          </div>
        </div>

        <!-- Several platforms: one button each, email below a divider. -->
        <div v-else class="space-y-3">
          <button v-if="options.microsoft && allows('microsoft')" :class="buttonClass" :disabled="auth.oauthRedirecting" @click="start('microsoft')">
            <svg class="h-4 w-4 shrink-0" viewBox="0 0 21 21" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            {{ pending === 'microsoft' ? t('signInRedirectingTo', { provider: 'Microsoft' }) : t('signInWithMicrosoft') }}
          </button>

          <button v-if="options.google && allows('google')" :class="buttonClass" :disabled="auth.oauthRedirecting" @click="start('google')">
            <svg class="h-4 w-4 shrink-0" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            {{ pending === 'google' ? t('signInRedirectingTo', { provider: 'Google' }) : t('signInWithGoogle') }}
          </button>

          <div v-if="auth.canUseOAuth && allows('lark')">
            <button :class="buttonClass" :disabled="auth.oauthRedirecting" @click="start('lark')">
              <img src="/lark-logo.png" alt="" aria-hidden="true" class="h-4 w-4 shrink-0" />
              {{ pending === 'lark' ? t('signInRedirecting') : t('signInContinueWithLark') }}
            </button>
          </div>

          <template v-if="options.email && allows('email')">
            <div v-if="!onlyPlatform" class="flex items-center gap-3 py-1" aria-hidden="true">
              <div class="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
              <span class="text-xs uppercase tracking-wider text-gray-400">{{ t('signInOr') }}</span>
              <div class="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            </div>

            <!-- Step 1: the address. -->
            <form v-if="emailStep === 'enter'" class="space-y-3" @submit.prevent="sendEmail">
              <label for="signin-email" class="block text-sm font-medium text-gray-700 dark:text-gray-300">{{ t('signInEmailLabel') }}</label>
              <input
                id="signin-email"
                v-model="email"
                type="email"
                autocomplete="email"
                inputmode="email"
                required
                :placeholder="t('signInEmailPlaceholder')"
                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div v-if="options.turnstileSiteKey" ref="turnstileEl" />
              <p v-if="turnstile.failed.value" class="text-xs text-amber-700 dark:text-amber-400">{{ t('signInTurnstileFailed') }}</p>
              <button
                type="submit"
                :disabled="!canSend"
                class="flex items-center justify-center gap-2 w-full px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Loader2 v-if="emailBusy" :size="16" class="animate-spin" />
                <Mail v-else :size="16" />
                {{ t('signInEmailSend') }}
              </button>
              <p class="text-xs text-gray-500 dark:text-gray-400 text-center">{{ t('signInEmailHint') }}</p>
            </form>

            <!-- Step 2: check your inbox; the code is for another device. -->
            <form v-else class="space-y-3" @submit.prevent="verifyCode">
              <div class="flex items-start gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 px-3 py-2.5 text-sm text-blue-800 dark:text-blue-200">
                <CheckCircle2 :size="16" class="mt-0.5 shrink-0" aria-hidden="true" />
                <span>{{ t('signInEmailSent', { email: email.trim() }) }}</span>
              </div>
              <label for="signin-code" class="block text-sm font-medium text-gray-700 dark:text-gray-300">{{ t('signInEmailCodeLabel') }}</label>
              <input
                id="signin-code"
                v-model="code"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxlength="6"
                pattern="[0-9]{6}"
                placeholder="123456"
                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 text-center text-lg tracking-[0.4em] tabular-nums text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                :disabled="!codeValid || emailBusy"
                class="flex items-center justify-center gap-2 w-full px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Loader2 v-if="emailBusy" :size="16" class="animate-spin" />
                {{ t('signInEmailVerify') }}
              </button>
              <button type="button" class="flex items-center justify-center gap-1 w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" @click="useAnotherEmail">
                <ArrowLeft :size="14" aria-hidden="true" />
                {{ t('signInEmailUseAnother') }}
              </button>
              <!-- A mail the provider took can still be held up at the recipient's end:
                   say where to look, and that a new code is one step back. -->
              <p class="text-xs text-gray-500 dark:text-gray-400 text-center">{{ t('signInEmailNoMail') }}</p>
            </form>

            <p v-if="emailError" role="alert" class="text-sm text-red-600 dark:text-red-400 text-center">{{ message(emailError) }}</p>
          </template>
        </div>
      </div>

      <footer class="mt-10 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-gray-400 dark:text-gray-500">
        <a
          v-for="link in legalLinks"
          :key="link.href"
          :href="link.href"
          target="_blank"
          rel="noopener"
          class="hover:text-gray-600 dark:hover:text-gray-300 hover:underline"
        >
          {{ link.label }}
        </a>
        <span>&copy; {{ new Date().getFullYear() }} PT Karunia Solusi Informatika</span>
      </footer>
    </main>
  </div>
</template>
